/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    this.container = document.getElementById("Facet-Drawer");
    if (!this.container || this.container.facetsController) return;
    this.container.facetsController = this;
    this.isOpen = false;
    this.inertElements = [];
    document.addEventListener("click", event => {
      const trigger = event.target.closest("#Facets-Toggle, [data-quick-all]");
      if (trigger) {
        event.preventDefault();
        this.returnSelector = trigger.matches('[data-quick-all]') ? '[data-quick-all]' : '#Facets-Toggle';
        if (this.isOpen) this.close();
        else {
          this.returnFocus = trigger;
          this.container.classList.add("active");
          document.body.classList.add("open-cc");
          this.sync();
        }
      } else if (this.isOpen && (event.target.closest('[data-facets-close]') || event.target.closest('.click-capture'))) {
        event.preventDefault();
        this.close();
      }
    });
    document.addEventListener("keydown", event => {
      if (!this.isOpen) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      } else if (event.key === "Tab") {
        const items = this.focusable();
        const first = items[0], last = items[items.length - 1];
        if (!first) { event.preventDefault(); this.container.focus(); return; }
        if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
    }, true);
    document.addEventListener("focusin", event => {
      if (this.isOpen && !this.container.contains(event.target)) this.focusable()[0]?.focus();
    });
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.container, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('pageshow', event => { if (event.persisted) this.close(false); });
  }
  focusable() {
    return [...this.container.querySelectorAll('a[href], button, input, select, summary, [tabindex="0"]')]
      .filter(item => !item.disabled && !item.closest('[inert], [hidden]') && item.getClientRects().length && getComputedStyle(item).visibility !== 'hidden');
  }
  sync() {
    const open = this.container.classList.contains('active');
    if (open === this.isOpen) return;
    this.isOpen = open;
    this.container.inert = !open;
    this.container.setAttribute('aria-hidden', String(!open));
    if (open) {
      this.container.setAttribute('aria-modal', 'true');
      this.hadScrollLock = document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      for (let branch = this.container; branch && branch !== document.body; branch = branch.parentElement) {
        for (const sibling of branch.parentElement.children) {
          if (sibling !== branch && !sibling.inert && !sibling.matches('script, style, link, .click-capture')) {
            sibling.inert = true;
            this.inertElements.push(sibling);
          }
        }
      }
      this.container.querySelector('.side-panel-content')?.scrollTo(0, 0);
      this.container.querySelector('[data-facets-close]')?.focus({ preventScroll: true });
    } else {
      this.container.removeAttribute('aria-modal');
      if (!this.hadScrollLock) document.body.classList.remove('overflow-hidden');
      this.inertElements.forEach(element => { element.inert = false; });
      this.inertElements = [];
      if (this.restoreFocus !== false) {
        const target = document.querySelector(this.returnSelector || '#Facets-Toggle');
        const fallback = document.querySelector('#Facets-Toggle');
        (target?.getClientRects().length ? target : fallback)?.focus({ preventScroll: true });
      }
      this.restoreFocus = true;
    }
    document.querySelectorAll('#Facets-Toggle, [data-quick-all]').forEach(item => item.setAttribute('aria-expanded', String(open)));
  }
  close(restoreFocus = true) {
    this.restoreFocus = restoreFocus;
    this.container.classList.remove('active');
    document.body.classList.remove('open-cc');
    this.sync();
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    this.querySelector("form").addEventListener(
      "input",
      this.debouncedOnSubmit.bind(this),
    );
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName("thb-filter-count");
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");

    for (var item of container) {
      item.classList.add("loading");
    }

    const requestId = ++FacetFiltersForm.requestId;
    const requests = sections.map((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        return FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId);
      } else {
        return FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    return Promise.all(requests);
  }

  static renderSectionFromFetch(url, event, requestId) {
    return fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load collection');
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.requestId) return;
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        new FacetsToggle();
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    if (requestId !== FacetFiltersForm.requestId) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    new FacetsToggle();
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    let top =
      document.getElementById("ProductGridContainer").getBoundingClientRect()
        .top +
      document.documentElement.scrollTop -
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-height",
        ),
        10,
      ) -
      30;

    window.scrollTo({
      top: top,
      left: 0,
      behavior: "smooth",
    });
  }

  static renderProductCount(html) {
    const status = document.getElementById('CollectionResultsStatus');
    if (status) status.textContent = document.querySelector('[data-quick-count]')?.textContent.trim() || '';
    const countHtml = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount");
    const container = document.getElementsByClassName("thb-filter-count");

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove("loading");
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row",
    );
    const matchesIndex = (element) => {
      const jsFilter = event
        ? event.target.closest("collapsible-row")
        : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element),
    );
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(
        `collapsible-row[data-index="${element.dataset.index}"]`,
      ).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    new FacetsToggle();

    if (countsToRender)
      FacetFiltersForm.renderCounts(
        countsToRender,
        event.target.closest("collapsible-row"),
      );
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const clear = document.querySelector('.mobile-filters-clear');
    const nextClear = html.querySelector('.mobile-filters-clear');
    if (clear && nextClear) clear.href = nextClear.href;
    const mobileElementSelectors = [".mobile-filter-count"];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      {
        searchParams,
      },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest("form"));
    const searchParams = new URLSearchParams(formData);
    if (!searchParams.has('sort_by')) {
      const sort = document.getElementById('SortByBar')?.value;
      if (sort) searchParams.set('sort_by', sort);
    }
    if (searchParams.get("filter.v.price.gte") === "0.00") {
      searchParams.delete("filter.v.price.gte");
    }
    if (document.querySelector(".price_slider")) {
      if (
        searchParams.get("filter.v.price.lte") ===
        parseFloat(document.querySelector(".price_slider").dataset.max).toFixed(
          2,
        )
      ) {
        searchParams.delete("filter.v.price.lte");
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event).catch(() => location.assign(location.pathname + '?' + searchParams));
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    const fromGrid = !!event.currentTarget.closest('#ProductGridContainer');
    FacetFiltersForm.renderPage(url).then(() => {
      if (fromGrid) document.querySelector('[data-quick-all]')?.focus({ preventScroll: true });
    }).catch(() => location.assign(event.currentTarget.href));
  }
}

FacetFiltersForm.requestId = 0;
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("a").forEach((item) => {
      item.addEventListener("click", (event) => {
        event.preventDefault();
        const form =
          this.closest("facet-filters-form") ||
          document.querySelector("facet-filters-form");
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define("facet-remove", FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector(".price_slider"),
      amounts = this.querySelector(".price_slider_amount"),
      args = {
        start: [
          parseFloat(slider.dataset.minValue || 0),
          parseFloat(slider.dataset.maxValue || slider.dataset.max),
        ],
        connect: true,
        step: 10,
        direction: document.dir,
        range: {
          min: 0,
          max: parseFloat(slider.dataset.max),
        },
      },
      event = new CustomEvent("input"),
      form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");

    if (slider.classList.contains("noUi-target")) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.querySelectorAll('[role="slider"]').forEach((handle, index) => {
      const input = amounts.querySelector(index === 0 ? '.field__input_min' : '.field__input_max');
      if (input?.getAttribute('aria-label')) handle.setAttribute('aria-label', input.getAttribute('aria-label'));
    });
    slider.noUiSlider.on("update", function (values) {
      amounts.querySelector(".field__input_min").value = values[0];
      amounts.querySelector(".field__input_max").value = values[1];
    });
    slider.noUiSlider.on("change", function (values) {
      form.querySelector("form").dispatchEvent(event);
    });
  }
}
customElements.define("price-slider", PriceSlider);

window.addEventListener("load", () => {
  new FacetsToggle();
});
